import { LoggerService } from "@bfx/logger";
const logger = LoggerService.for(__dirname);

import * as CubiqTS from "shared/cubiq-model-multi-asset";
import { BFXNotificationType } from "../core/notifications/BFXNotificationType";
import { BFXAppNotificationType } from "../core/notifications/BFXAppNotificationType";
import { BFXNotificationSubType } from "../core/notifications/BFXNotificationSubType";
import { BFXAppNotificationSubType } from "../core/notifications/BFXAppNotificationSubType";
import { CubiqWorker } from "shared/cubiq-services/worker/CubiqWorker";
import { Injectable } from "@angular/core";
import { NotificationTransactionAdapter } from "shared/cubiq-services/adapter/interventionBlotter/NotificationTransactionAdapter";
import { CcyLookupService } from "shared/cubiq-services/lookup/model/impl/CcyLookupService";
import { CcyPairLookupService } from "shared/cubiq-services/lookup/model/impl/CcyPairLookupService";
import { BmarkOrderData } from "../benchmarks/BmarkOrderData.model";

export const EXTENDED_INFO_IS_ORDER_ENTRY: string = "IS_ORDER_ENTRY";

@Injectable()
export class NotificationEventProcessorService {
  private notificationTransactionAdapter: NotificationTransactionAdapter;

  constructor(
    private cubiqWorker: CubiqWorker,
    private _ccyLookupService: CcyLookupService,
    private _ccyPairLookupService: CcyPairLookupService,
  ) {
    this.notificationTransactionAdapter = new NotificationTransactionAdapter(
      this._ccyLookupService,
      this._ccyPairLookupService,
    );
  }

  // for Intervention event

  public populateExtraData(
    notifType: BFXNotificationType,
    notifSubType: BFXNotificationSubType,
    interventionEvent: CubiqTS.InterventionEventImpl,
  ) {
    let data = {};
    let extraData = {};
    data["notifType"] = notifType;
    data["notifSubType"] = notifSubType;
    extraData["bodyContent"] = this.notificationTransactionAdapter.adapt(interventionEvent);
    extraData["clientIntent"] = this.notificationTransactionAdapter.getClientIntent(
      interventionEvent.orderData.clientIntent,
    );
    extraData["rateRequest"] =
      this.notificationTransactionAdapter.getRequestRate(interventionEvent);
    extraData["requestedOrderType"] = this.notificationTransactionAdapter.resolveRequestOrderType(
      interventionEvent.orderData.requestedOrderType,
    );
    data["extraData"] = extraData;
    return data;
  }

  public convertInterventionEventTypeToNotifType(
    eventType: CubiqTS.InterventionOperationType,
  ): BFXNotificationType {
    switch (eventType) {
      case CubiqTS.InterventionOperationType.NEW:
        return BFXNotificationType.DEALER_INTERVENTION;
    }
    return undefined;
  }

  public convertInterventionEventTypeToSubType(
    eventType: CubiqTS.DIReason,
  ): BFXNotificationSubType {
    switch (eventType) {
      case CubiqTS.DIReason.CREDIT_CANCEL_FAIL:
      case CubiqTS.DIReason.CREDIT_CANCEL_TIMEOUT:
      case CubiqTS.DIReason.CREDIT_EXPIRY_FAIL:
      case CubiqTS.DIReason.CREDIT_EXPIRY_TIMEOUT:
        return BFXNotificationSubType.CREDIT_FAIL_CANCELEXPIRY;
      case CubiqTS.DIReason.ENTRY_CREDIT_FAIL:
      case CubiqTS.DIReason.ENTRY_CREDIT_PROXIMITY_FAIL:
        return BFXNotificationSubType.CREDIT_FAIL_ENTRY;
      case CubiqTS.DIReason.MODIFY_CREDIT_FAIL:
      case CubiqTS.DIReason.MODIFY_CREDIT_PROXIMITY_FAIL:
        return BFXNotificationSubType.MODIFY_FAIL_ENTRY;
      case CubiqTS.DIReason.FILL_CREDIT_FAIL:
        return BFXNotificationSubType.CREDIT_FAIL_FILL;
      case CubiqTS.DIReason.CREDIT_REJECT_FAIL:
      case CubiqTS.DIReason.CREDIT_REJECT_TIMEOUT:
        return BFXNotificationSubType.CREDIT_FAIL_REJECTED;
      case CubiqTS.DIReason.DAILY_REVAL_CREDIT_FAIL:
      case CubiqTS.DIReason.PROXIMITY_REVAL_CREDIT_FAIL:
        return BFXNotificationSubType.CREDIT_FAIL_REVAL;
      case CubiqTS.DIReason.FORCE_TO_DI:
        return BFXNotificationSubType.CREDIT_FAIL_FORCE_DI;
    }
    return undefined;
  }

  // for Order life cycle
  public determineNotifType(
    event: CubiqTS.OrderLifeCycleEventImpl,
    orderDataStored: CubiqTS.OrderDataImpl,
  ): {
    type: BFXNotificationType;
    subType: BFXNotificationSubType;
    appType: BFXAppNotificationType;
  } {
    let notifType: BFXNotificationType = this.convertLifeCycleEventTypeToNotifType(
      event.eventTypeId,
    );
    let appNotifType: BFXAppNotificationType = this.convertEventTypeToAppNotifType(
      event.eventTypeId,
    );
    if (event.eventTypeId == CubiqTS.FxLifeCycleEventType.ACTIVE) {
      let existingOrder = orderDataStored; //this.base.getBmarkOrderDataForOrderId(event.orderData.orderId.toString());
      if (existingOrder) {
        if (existingOrder.lockType === CubiqTS.LockType.MODIFY_LOCK) {
          if (
            existingOrder.lockUserId &&
            existingOrder.lockUserId.toString() === this.cubiqWorker.getUserId()
          ) {
            notifType = undefined;
            appNotifType = undefined;
          } else {
            notifType = BFXNotificationType.MODIFIED;
            appNotifType = BFXAppNotificationType.MODIFIED;
          }
        } else if (
          CubiqTS.OrderState.PENDING == existingOrder.orderState ||
          CubiqTS.OrderState.SUSPENDED == existingOrder.orderState
        ) {
          notifType = BFXNotificationType.ACTIVATED;
          appNotifType = BFXAppNotificationType.ACTIVATED;
        }
      }
    }
    let notifSubType: BFXNotificationSubType;
    if (
      notifType === BFXNotificationType.ACCEPTANCE_NEEDED ||
      notifType === BFXNotificationType.BMARK_NEW_ORDERS
    ) {
      notifSubType = this.convertOrderFlagsToSubType(event.orderData);
      if (notifSubType === undefined) {
        // unknown type of acceptance so we dont want to show a corrupted notification
        notifType = undefined;
      } else if (
        notifSubType === BFXNotificationSubType.ACCEPTANCE_TRANSFER &&
        event.orderData.transferUserId &&
        event.orderData.transferUserId.toString() === this.cubiqWorker.getUserId()
      ) {
        notifType = undefined;
        notifSubType = undefined;
      } else if (
        notifSubType === BFXNotificationSubType.ACCEPTANCE_NEW &&
        event.orderData.entryUserId &&
        event.orderData.entryUserId.toString() === this.cubiqWorker.getUserId()
      ) {
        notifType = undefined;
        notifSubType = undefined;
      } else if (
        (notifSubType === BFXNotificationSubType.ACCEPTANCE_MODIFY_OUT ||
          notifSubType === BFXNotificationSubType.ACCEPTANCE_CANCEL ||
          notifSubType === BFXNotificationSubType.ACCEPTANCE_MODIFY_IN) &&
        (event.orderData.lockUserId?.toString() === this.cubiqWorker.getUserId() ||
          event.orderData.cancelUserId?.toString() === this.cubiqWorker.getUserId())
      ) {
        notifType = undefined;
        notifSubType = undefined;
      }
    } else if (notifType === BFXNotificationType.CANCELLED) {
      if (
        event.orderData.cancelUserId &&
        event.orderData.cancelUserId.toString() === this.cubiqWorker.getUserId()
      ) {
        notifType = undefined;
        notifSubType = undefined;
        appNotifType = undefined;
      }
    } else if (notifType === BFXNotificationType.TRIGGERED) {
      let existingOrder = orderDataStored; //this.base.getBmarkOrderDataForOrderId(event.orderData.orderId.toString());
      if (existingOrder && existingOrder.orderState === CubiqTS.OrderState.TRIGGERED) {
        if (
          existingOrder.leavesQty &&
          existingOrder.leavesQty.isGreaterThan(event.orderData.leavesQty)
        ) {
          // this was a part fill dont show the notification
          notifType = undefined;
        }
        if (existingOrder.isAboutToSuspend != event.orderData.isAboutToSuspend) {
          notifType = undefined;
        }
      }
    }
    return { type: notifType, subType: notifSubType, appType: appNotifType };
  }

  convertEventTypeToAppNotifType(eventType: CubiqTS.FxLifeCycleEventType): BFXAppNotificationType {
    switch (eventType) {
      case CubiqTS.FxLifeCycleEventType.GFT_ACTIVATE:
        return BFXAppNotificationType.ACTIVATED;
      case CubiqTS.FxLifeCycleEventType.CANCELLED:
        return BFXAppNotificationType.CANCELLED;
      case CubiqTS.FxLifeCycleEventType.EXPIRED:
        return BFXAppNotificationType.EXPIRED;
      case CubiqTS.FxLifeCycleEventType.SUSPENDED:
        return BFXAppNotificationType.SUSPENDED;
    }
    return undefined;
  }

  convertOrderFlagsToSubType(orderData: CubiqTS.OrderDataImpl): BFXNotificationSubType {
    switch (orderData.orderState) {
      case CubiqTS.OrderState.ACCEPTANCE_NEEDED:
        if (orderData.lockType === CubiqTS.LockType.MODIFY_LOCK) {
          return BFXNotificationSubType.ACCEPTANCE_MODIFY_IN;
        }
        return BFXNotificationSubType.ACCEPTANCE_NEW;
      case CubiqTS.OrderState.CANCEL_ACCEPTANCE_NEEDED:
        return BFXNotificationSubType.ACCEPTANCE_CANCEL;
      case CubiqTS.OrderState.MODIFY_ACCEPTANCE_NEEDED:
        return BFXNotificationSubType.ACCEPTANCE_MODIFY_OUT;
      case CubiqTS.OrderState.MIGRATION_ACCEPTANCE_NEEDED:
        return BFXNotificationSubType.ACCEPTANCE_MIGRATION;
      case CubiqTS.OrderState.EXPIRY_ACCEPTANCE_NEEDED:
        return BFXNotificationSubType.ACCEPTANCE_EXPIRY;
      case CubiqTS.OrderState.TRIGGERED_EXPIRY_ACCEPTANCE_NEEDED:
        return BFXNotificationSubType.ACCEPTANCE_TRIGGER_EXPIRY;
    }
    // temp transfer check
    if (orderData.targetDeskId && orderData.targetDeskId.greaterThan(-1)) {
      return BFXNotificationSubType.ACCEPTANCE_TRANSFER;
    }
    return undefined;
  }

  // for benchmark events
  // CD  TODO: needs refactoring
  public determineBmarkNotifEvent(
    event:
      | CubiqTS.FixingEventImpl
      | CubiqTS.OrderLifeCycleEventImpl
      | CubiqTS.UnsolicitedCancelLifeCycleEventImpl,
    orderData?: BmarkOrderData,
    existingOrder?: BmarkOrderData,
  ): {
    type: BFXNotificationType;
    subType: BFXNotificationSubType;
    appType: BFXAppNotificationType;
  } {
    let notifType: BFXNotificationType;
    let notifSubType: BFXNotificationSubType;
    let appNotifType: BFXAppNotificationType;
    if (event instanceof CubiqTS.FixingEventImpl) {
      switch (event.fixingState) {
        case CubiqTS.FixingState.SOFT_CUTOFF:
          notifType = BFXNotificationType.BMARK_SOFT_CUT_OFF;
          break;
        case CubiqTS.FixingState.HARD_CUTOFF:
          notifType = BFXNotificationType.BMARK_HARD_CUT_OFF;
          break;
        case CubiqTS.FixingState.TRIGGERED:
          notifType = BFXNotificationType.BMARK_TRIGGERED;
          break;
        case CubiqTS.FixingState.COMPLETED:
        case CubiqTS.FixingState.FIXING_HIT:
          notifType = undefined;
          break;
        default:
          logger.out.error(
            `ERROR: ${this.constructor.name} Unhandled fixing state:  ${
              CubiqTS.FixingState[event.fixingState]
            }`,
          );
          break;
      }
    } else if (
      event instanceof CubiqTS.OrderLifeCycleEventImpl ||
      event instanceof CubiqTS.UnsolicitedCancelLifeCycleEventImpl
    ) {
      notifType = this.convertBmarkOrderLifeCycleEventType(event);
      appNotifType = this.convertBmarkEventTypeToAppNotifType(event.eventTypeId);
      notifSubType = this.convertBmarkOrderLifeCycleEventTypeToNotifSubType(orderData);
      if (orderData != null) {
        if (event.eventTypeId == CubiqTS.FxLifeCycleEventType.ACTIVE) {
          if (existingOrder != null) {
            if (existingOrder.lockType === CubiqTS.LockType.MODIFY_LOCK) {
              if (
                existingOrder.lockUserId &&
                existingOrder.lockUserId.toString() === this.cubiqWorker.getUserId()
              ) {
                notifType = undefined;
                appNotifType = undefined;
              } else {
                notifType = BFXNotificationType.BMARK_MODIFIED;
                appNotifType = BFXAppNotificationType.APP_BMARK_MODIFIED;
              }
            }
          }
        } else if (event.eventTypeId == CubiqTS.FxLifeCycleEventType.CANCELLED) {
          //jmh
          if (
            orderData.cancelUserId != null &&
            orderData.cancelUserId.toString() === this.cubiqWorker.getUserId()
          ) {
            notifType = undefined;
            notifSubType = undefined;
            appNotifType = undefined;
          }
        } else if (
          notifSubType === BFXNotificationSubType.ACCEPTANCE_NEW &&
          orderData.entryUserId &&
          orderData.entryUserId.toString() === this.cubiqWorker.getUserId()
        ) {
          notifType = undefined;
          notifSubType = undefined;
        } else if (
          notifSubType === BFXNotificationSubType.ACCEPTANCE_CANCEL &&
          orderData.cancelUserId &&
          orderData.cancelUserId.toString() === this.cubiqWorker.getUserId()
        ) {
          notifType = undefined;
          notifSubType = undefined;
        }
      }

      if (event instanceof CubiqTS.OrderLifeCycleEventImpl) {
        let orderData = event.orderData;
        let entryNotification: boolean = orderData?.extendedInfo?.has(EXTENDED_INFO_IS_ORDER_ENTRY);
        if (
          entryNotification &&
          orderData?.entryUserId?.toString() != this.cubiqWorker.getUserId()
        ) {
          notifType = BFXNotificationType.BMARK_ORDER_ENTERED;
          notifSubType = BFXNotificationSubType.BENCHMARK_ENTRY;
          appNotifType = BFXAppNotificationType.APP_BMARK_ENTERED;
        }
      }
    }

    return { type: notifType, subType: notifSubType, appType: appNotifType };
  }

  convertBmarkOrderLifeCycleEventType(
    event: CubiqTS.OrderLifeCycleEventImpl | CubiqTS.UnsolicitedCancelLifeCycleEventImpl,
  ): BFXNotificationType {
    let eventType: CubiqTS.FxLifeCycleEventType = event.eventTypeId;
    if (eventType === CubiqTS.FxLifeCycleEventType.CANCELLED) {
      return BFXNotificationType.BMARK_CANCELLED;
    } else if (eventType === CubiqTS.FxLifeCycleEventType.UNSOLICITED_CANCEL) {
      return BFXNotificationType.UNSOLICITED_CANCEL;
    } else if (eventType === CubiqTS.FxLifeCycleEventType.TI_REQUIRED) {
      return BFXNotificationType.BMARK_NEW_ORDERS;
    } else if (eventType === CubiqTS.FxLifeCycleEventType.PENDING_CANCELLATION) {
      return BFXNotificationType.BMARK_NEW_ORDERS;
    }
    return undefined;
  }

  convertBmarkOrderLifeCycleEventTypeToNotifSubType(
    orderData: CubiqTS.OrderDataImpl,
  ): BFXNotificationSubType {
    if (
      orderData.orderState === CubiqTS.OrderState.ACCEPTANCE_NEEDED ||
      orderData.orderState === CubiqTS.OrderState.CANCEL_ACCEPTANCE_NEEDED
    ) {
      if (orderData.modifyInAcceptanceFg) {
        return BFXNotificationSubType.ACCEPTANCE_MODIFY_IN;
      }
      if (orderData.orderState === CubiqTS.OrderState.CANCEL_ACCEPTANCE_NEEDED) {
        return BFXNotificationSubType.ACCEPTANCE_CANCEL;
      }
      return BFXNotificationSubType.ACCEPTANCE_NEW;
    }
    return undefined;
  }

  convertBmarkEventTypeToAppNotifType(
    eventType: CubiqTS.FxLifeCycleEventType,
  ): BFXAppNotificationType {
    if (eventType === CubiqTS.FxLifeCycleEventType.CANCELLED) {
      return BFXAppNotificationType.APP_BMARK_CANCELLED;
    } else if (eventType === CubiqTS.FxLifeCycleEventType.UNSOLICITED_CANCEL) {
      return BFXAppNotificationType.APP_BMARK_UNSOLICITED_CANCEL;
    }
    return undefined;
  }

  // partial implementation, doing triggered first so others can be added as stories are hit.
  convertLifeCycleEventTypeToNotifType(
    eventType: CubiqTS.FxLifeCycleEventType,
  ): BFXNotificationType {
    switch (eventType) {
      case CubiqTS.FxLifeCycleEventType.TI_REQUIRED: //should the other acceptance be here also?
      case CubiqTS.FxLifeCycleEventType.PENDING_CANCELLATION:
        return BFXNotificationType.ACCEPTANCE_NEEDED;
      case CubiqTS.FxLifeCycleEventType.GFT_ACTIVATE:
        return BFXNotificationType.ACTIVATED;
      case CubiqTS.FxLifeCycleEventType.CANCELLED:
        return BFXNotificationType.CANCELLED;
      case CubiqTS.FxLifeCycleEventType.UNSOLICITED_CANCEL:
        return BFXNotificationType.UNSOLICITED_CANCEL;
      case CubiqTS.FxLifeCycleEventType.EXPIRED:
        return BFXNotificationType.EXPIRED;
      case CubiqTS.FxLifeCycleEventType.TRIGGERED:
        return BFXNotificationType.TRIGGERED;
      case CubiqTS.FxLifeCycleEventType.SUSPENDED:
        return BFXNotificationType.SUSPENDED;
      case CubiqTS.FxLifeCycleEventType.EXPIRING:
        return BFXNotificationType.EXPIRING;
      case CubiqTS.FxLifeCycleEventType.SUSPENDING:
        return BFXNotificationType.SUSPENDING;
      case CubiqTS.FxLifeCycleEventType.ACTIVATING:
        return BFXNotificationType.ACTIVATING;
    }
    return undefined;
  }
}
